// ** Modules
import React from 'react';
import Image from 'next/image';

// ** UI
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import { Icon } from '@/components/common/icons';

// ** Utils
import { normalizeDateLabel } from '@/utils/shared/stringUtils';

// ** Types
import type { UploadFile } from '@/types/graphql';

type MediaCardProps = {
	type: string;
	title: string;
	description?: string | null;
	imageData?: UploadFile | null;
	imageUrl?: string | null;
	host: string;
	slug: string;
	publishedAt?: string | null;
	typeLabel?: string | null;
	ctaLabel?: string;
	fluidWidth?: boolean;
	basePath?: string;
};

const MediaCard = ({
	type,
	host,
	slug,
	title,
	basePath,
	imageUrl,
	ctaLabel = 'Mehr erfahren',
	fluidWidth = false,
	imageData,
	typeLabel,
	description,
	publishedAt
}: MediaCardProps) => {
	const theme = useTheme();
	const sp = theme.spacing;

	const href = React.useMemo(() => {
		if (!slug) return basePath || '/';
		if (slug.startsWith('http')) return slug;

		// Clean paths
		const cleanBase = (basePath || '').replace(/\/$/, '');
		const cleanSlug = slug.replace(/^\//, '');

		// Landing Page check
		const baseSegments: string[] = cleanBase.split('/');
		const lastBaseSegment = baseSegments[baseSegments.length - 1];

		if (cleanSlug === lastBaseSegment) {
			return cleanBase;
		}

		// News deatils routing
		const isEvent = type?.toLowerCase() === 'event';

		if (!isEvent) {
			// Only inject /news/ if it's a detail page (slug !== base)
			const newsSegment = cleanBase?.endsWith('/news') ? '' : '/news';
			return `${cleanBase}${newsSegment}/${cleanSlug}`;
		}

		// Event routinng
		return `${cleanBase}/${cleanSlug}`;
	}, [basePath, slug, type]);

	const dateLabel = normalizeDateLabel(publishedAt);
	const displayType = typeLabel?.trim() || 'Media';
	const metaLabel = dateLabel ? `${displayType} | ${dateLabel}` : displayType;

	const titleLineHeight = 1.2;
	const titleMaxLines = 2;
	const descriptionLineHeight = 1.5;

	return (
		<Card
			className="MediaCard-root"
			sx={{
				width: '100%',
				minWidth: fluidWidth ? 0 : sp(296 / 4),
				maxWidth: fluidWidth ? 'none' : { xs: sp(361 / 4), md: sp(416 / 4) },
				height: { xs: sp(430 / 4), md: sp(560 / 4) },
				borderRadius: sp(2),
				backgroundColor: theme.palette.common.white,
				overflow: 'hidden',
				display: 'flex',
				flexDirection: 'column',
				boxShadow: theme.shadows[1],
				transition: 'box-shadow 220ms ease, transform 220ms ease',
				transform: 'translateY(0px)',
				'@media (hover: hover) and (pointer: fine)': {
					'&:hover': {
						transform: 'translateY(-2px)',
						boxShadow: theme.shadows[4]
					},
					'&:hover .MediaCard-imageMedia': {
						transform: 'scale(1.04)'
					}
				},
				'&:focus-within': {
					transform: 'translateY(-2px)',
					boxShadow: theme.shadows[3]
				},
				'&:focus-within .MediaCard-imageMedia': {
					transform: 'scale(1.04)'
				},
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none',
					'&:hover': { transform: 'none' },
					'&:focus-within': { transform: 'none' },
					'&:hover .MediaCard-imageMedia': { transform: 'none' },
					'&:focus-within .MediaCard-imageMedia': { transform: 'none' }
				}
			}}
		>
			<Box
				component={Link}
				href={href}
				aria-label={`${title} lesen`}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					color: 'inherit',
					textDecoration: 'none',
					'&:focusVisible': {
						outline: `2px solid ${theme.palette.primary.main}`,
						outlineOffset: '-2px'
					}
				}}
			>
				{/* Image */}
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						height: { xs: sp(188 / 4), md: sp(250 / 4) },
						flexShrink: 0,
						backgroundColor: theme.palette.grey[200],
						'& .MediaCard-imageMedia': {
							transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
							transform: 'scale(1)',
							willChange: 'transform'
						},
						'@media (prefers-reduced-motion: reduce)': {
							'& .MediaCard-imageMedia': {
								transition: 'none'
							}
						}
					}}
				>
					{!imageData && imageUrl ? (
						<Image
							src={imageUrl}
							alt={title}
							fill
							sizes="(max-width: 959px) 361px, 416px"
							className="MediaCard-imageMedia"
							style={{ objectFit: 'cover' }}
						/>
					) : (
						<StrapiImageRenderer
							host={host}
							imageData={imageData}
							alt={title}
							fill
							className="MediaCard-imageMedia"
							sizes="(max-width: 959px) 361px, 416px"
							style={{ objectFit: 'cover' }}
						/>
					)}
				</Box>

				{/* Label */}
				<Box
					sx={{
						pt: { xs: sp(6), md: sp(8) },
						pb: { xs: sp(6), md: sp(8) },
						px: { xs: sp(4), md: sp(8) },
						display: 'flex',
						flexDirection: 'column',
						flex: '1 1 auto',
						minHeight: 0,
						overflow: 'hidden'
					}}
				>
					<Typography
						variant="caption"
						component="div"
						sx={{
							color: theme.palette.text.secondary ?? theme.palette.text.primary,
							fontSize: sp(3.5),
							fontWeight: 400,
							lineHeight: { xs: '1.25rem', md: '1.125rem' },
							letterSpacing: '-0.05px',
							display: 'block',
							flexShrink: 0,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							minWidth: 0
						}}
					>
						{metaLabel}
					</Typography>

					{/* Title */}
					<Typography
						variant="title-sm"
						component="h3"
						sx={{
							mt: { xs: sp(3), md: sp(4) },
							color: theme.palette.common.black,
							fontWeight: 400,
							letterSpacing: { xs: '-0.45px', md: '-0.64px' },
							lineHeight: titleLineHeight,
							display: '-webkit-box',
							WebkitLineClamp: titleMaxLines,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							overflowWrap: 'anywhere',
							minHeight: `calc(${titleMaxLines} * ${titleLineHeight}em)`,
							maxHeight: `calc(${titleMaxLines} * ${titleLineHeight}em)`
						}}
					>
						{title}
					</Typography>

					{/* Description */}
					<Typography
						variant="body-md"
						component="div"
						sx={{
							mt: { xs: sp(4), md: sp(6) },
							color: theme.palette.text.secondary ?? theme.palette.text.primary,
							fontWeight: 400,
							letterSpacing: '-0.05px',
							lineHeight: descriptionLineHeight,
							display: '-webkit-box',
							WebkitLineClamp: { xs: 2, md: 3 },
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							overflowWrap: 'anywhere',
							minHeight: {
								xs: `calc(2 * ${descriptionLineHeight}em)`,
								md: `calc(3 * ${descriptionLineHeight}em)`
							},
							maxHeight: {
								xs: `calc(2 * ${descriptionLineHeight}em)`,
								md: `calc(3 * ${descriptionLineHeight}em)`
							}
						}}
					>
						{description ?? ''}
					</Typography>

					{/* CTA */}
					<Box
						sx={{
							mt: { xs: sp(6), md: sp(8) },
							minWidth: 0
						}}
					>
						<Box
							component="span"
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 2,
								maxWidth: '100%',
								minWidth: 0,
								position: 'relative',
								cursor: 'pointer',
								pb: '6px',
								'&::after': {
									content: '""',
									position: 'absolute',
									left: 0,
									right: 0,
									bottom: '3px',
									height: '1px',
									backgroundColor:
										theme.palette.primary.lighter ?? theme.palette.primary.light ?? theme.palette.primary.main,
									borderRadius: 999,
									transform: 'scaleX(0)',
									transformOrigin: 'left',
									opacity: 0.2,
									transition:
										'transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)',
									pointerEvents: 'none'
								},
								'.MediaCard-root:hover &, .MediaCard-root:focus-within &': {
									'&::after': {
										transform: 'scaleX(1)',
										opacity: 0.75
									},
									'& .MediaCard-ctaArrow': {
										transform: 'translateX(2px)'
									}
								},
								'& .MediaCard-ctaArrow': {
									display: 'inline-block',
									transform: 'translateX(0)',
									transition: 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)'
								},
								'@media (prefers-reduced-motion: reduce)': {
									'&::after': { transition: 'none' },
									'& .MediaCard-ctaArrow': { transition: 'none' }
								}
							}}
						>
							<Icon
								name="arrowSmallRight"
								className="MediaCard-ctaArrow"
								width={11}
								height={9}
								color={theme.palette.text.secondary ?? theme.palette.text.primary}
							/>
							<Typography
								variant="body-md"
								component="span"
								sx={{
									color: theme.palette.text.secondary ?? theme.palette.text.primary,
									fontWeight: 500,
									letterSpacing: '-0.05px'
								}}
							>
								<Box
									component="span"
									sx={{
										display: 'block',
										maxWidth: '100%',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									}}
								>
									{ctaLabel}
								</Box>
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
		</Card>
	);
};

export default MediaCard;
